import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown, Download, FileText } from "lucide-react";
import PropTypes from "prop-types";
import { toast } from "sonner";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import TranscriptionRequestResultsDialog from "components/modals/TranscriptionRequestResultsDialog";
import callConstants from "constants/Call";
import { formatError } from "helpers/formatError";
import CampaignTargetCallService from "services/CampaignTargetCall";

// Mirrors MAX_TRANSCRIPT_EXPORT_CALLS on the server, so an oversized selection
// disables the item instead of coming back as an error.
export const MAX_TRANSCRIPT_EXPORT_CALLS = 500;

// Mirrors MAX_TRANSCRIPTION_REQUEST_CALLS on the server. Far below the export's
// cap because each call commits on its own and dispatches a paid job.
export const MAX_TRANSCRIPTION_REQUEST_CALLS = 100;

const QUEUED = callConstants.TRANSCRIPT_REQUEST_OUTCOMES.QUEUED;

const isDownloadable = row =>
  row.transcript_status === callConstants.TRANSCRIPT_STATUSES.COMPLETED;

/**
 * Bulk actions for the calls table, rendered in the toolbar once rows are
 * selected.
 */
export function CallsTableBulkActions({
  selectedData = [],
  uncheckAll,
  selectedIds = [],
  invalidateData,
}) {
  const [requestResults, setRequestResults] = useState(null);

  const downloadableIds = selectedData
    .filter(isDownloadable)
    .map(row => row.id);
  const overTheCap = downloadableIds.length > MAX_TRANSCRIPT_EXPORT_CALLS;
  const overTheRequestCap =
    selectedIds.length > MAX_TRANSCRIPTION_REQUEST_CALLS;

  const { mutate: exportTranscripts, isPending } = useMutation({
    mutationFn: ids => CampaignTargetCallService.exportTranscripts(ids),
    onSuccess: () => {
      uncheckAll();
      toast.success(
        "Your download has started. You'll receive an email when it's " +
          "finished. You can also check the 'Data' tab for progress and " +
          "download."
      );
    },
    onError: err =>
      toast.error(formatError(err, "Failed to start the transcript export")),
  });

  const { mutate: requestTranscriptions, isPending: isRequesting } =
    useMutation({
      // Every selected call is sent: the row carries the disposition's name but
      // not its type, so only the server can answer whether one qualifies.
      mutationFn: ids => CampaignTargetCallService.requestTranscriptions(ids),
      onSuccess: async response => {
        // Captured before uncheckAll clears the selection the dialog reads
        // its call context from.
        const callsById = Object.fromEntries(
          selectedData.map(row => [row.id, row])
        );
        uncheckAll();
        await invalidateData?.();

        const queued = response.queued;
        if (queued) {
          toast.success(
            queued === 1
              ? "Transcription requested for 1 call."
              : `Transcription requested for ${queued} calls.`
          );
        }
        if (response.results.some(result => result.outcome !== QUEUED)) {
          setRequestResults({ ...response, callsById });
        }
      },
      onError: err =>
        toast.error(formatError(err, "Failed to request transcription")),
    });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={buttonVariants({ variant: "outline" })}
          disabled={selectedIds.length === 0}
        >
          Actions
          <ChevronDown />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="tw:w-auto">
          <DropdownMenuGroup>
            <DropdownMenuItem
              disabled={downloadableIds.length === 0 || overTheCap || isPending}
              // The menu stays open so the in-progress indicator is visible.
              closeOnClick={false}
              onClick={() => exportTranscripts(downloadableIds)}
            >
              {isPending ? <Spinner /> : <Download />}
              Download Transcripts
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={
                selectedIds.length === 0 || overTheRequestCap || isRequesting
              }
              closeOnClick={false}
              onClick={() => requestTranscriptions(selectedIds)}
            >
              {isRequesting ? <Spinner /> : <FileText />}
              Request Transcription
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {requestResults && (
        <TranscriptionRequestResultsDialog
          open
          onOpenChange={() => setRequestResults(null)}
          queued={requestResults.queued}
          minimumDurationSeconds={requestResults.minimum_duration_seconds}
          results={requestResults.results}
          callsById={requestResults.callsById}
        />
      )}
    </>
  );
}

CallsTableBulkActions.propTypes = {
  selectedIds: PropTypes.array,
  selectedData: PropTypes.array,
  uncheckAll: PropTypes.func.isRequired,
  invalidateData: PropTypes.func,
};

export default CallsTableBulkActions;
